import { Component, ElementRef, HostListener, Injector, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { BaseComponent } from 'src/app/common/base/base.component';
import { AbsiTemplateResource, AbsiTemplateTypeResource, AbsiTemplateVersionResource } from '../absi';
import { AbsiBuilderService } from './absi-builder.service';
import { AbsiBuilderSideComponent } from './absi-builder-side/absi-builder-side.component';
import { Observable, Subscription, takeUntil } from 'rxjs';
import { AbsiBuilderMainComponent } from './absi-builder-main/absi-builder-main.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { WFError } from '../../checklists/checklist-builder/checklist-builder.component';
import { AbsiTemplateTypeRefresh } from '../store/absi-template-type/absi-template-type.action';

@Component({
  selector: 'app-absi-builder',
  templateUrl: './absi-builder.component.html',
  styleUrls: ['./absi-builder.component.scss']
})
export class AbsiBuilderComponent extends BaseComponent implements OnInit {

  @ViewChild("sidenav") sidenav!: MatSidenav;
  @ViewChild("headerTop") headerTop!: ElementRef;
  @ViewChild(AbsiBuilderSideComponent) side!: AbsiBuilderSideComponent;
  @ViewChild(AbsiBuilderMainComponent) main!: AbsiBuilderMainComponent;


  disabled: boolean = false;

  resourceRequired: boolean = false;

  filterCtrl = new FormControl();

  headerOnTop: boolean = false;

  valueFilter!: string;
  createdOn!: string;

  hasErrors?: boolean;

  ///////
  tmpFilter!: string;
  condition!: string;
  filteredFilter: any[] = [];
  currentFilter: any;
  filter: any;

  loadingMessage!: string;

  version?: AbsiTemplateVersionResource | null;
  versions?: AbsiTemplateVersionResource[];
  versions$!: Observable<AbsiTemplateVersionResource[]>;
  versionsSubs!: Subscription;

  globalTemplate?: string;

  type?: AbsiTemplateTypeResource;

  //////

  errorMessages?: WFError[];
  private resizeTimer: any;

  constructor(
    protected override injector: Injector,
    private _builder: AbsiBuilderService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.moduleTitle = 'ABSI Template Builder';
    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkErrors();
    });
  }

  override ngOnDestroy(): void {
    this.versionsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Keeps the selected version in sync with the store while preserving the fully
   * hydrated workflow sections returned by GetTemplateVersionsByID.
   */
  loadVersions() {
    this.versionsSubs?.unsubscribe();
    this.versions$ = this.store.select(state => state.AbsiTemplateVersions.data);
    this.versionsSubs = this.versions$.subscribe(data => {
      if (data?.length) {
        this.versions = data;
        if (this.version) {
          const versionInList = this.versions.find(x => x.id == this.version?.id);
          if (versionInList) {
            this.version = this.version.wfTableConfig?.wfSectionConfigs?.length && !versionInList.wfTableConfig?.wfSectionConfigs?.length
              // The ByType endpoint returns a slim payload for speed; do not let
              // that refresh replace the already-loaded section tree.
              ? { ...versionInList, wfTableConfig: this.version.wfTableConfig }
              : versionInList;
          }
          this.cdRef.detectChanges();
        }
      }
    });
  }

  /**
   * Loads the complete template only when a row is selected from the slim list.
   * Re-selecting an already hydrated row is a no-op to avoid duplicate requests.
   */
  selectedRow(e: AbsiTemplateVersionResource) {
    if (!e.id) {
      return;
    }

    if (this.version?.id == e.id && this.version?.wfTableConfig?.wfSectionConfigs?.length) {
      return;
    }

    this._builder.GetTemplateVersionsByID(e.id).subscribe(data => {
      this.version = data;
      this.globalTemplate = this.version?.wfTableConfig?.name;
      this.cdRef.detectChanges();
      this.setHeights();
    }, error => {
      console.error(error);
    });
  }

  /**
   * Resets the builder when the template type changes and starts listening to
   * the version list for the selected type.
   */
  selectedType(type: AbsiTemplateTypeResource) {
    this.type = type;
    this.version = null;
    this.loadVersions();
    this.setHeights();
  }

  clear() {
  }

  create() {
    if (this.type) {
      this.version = null;
      let absiTemplate: AbsiTemplateResource = {
        name: 'ABSI',
        requesterId: this.getCurrentUser()?.id ?? -1,
        status: 0,
        type: this.type.id ?? 0
      };
      this._builder.CreateChecklistTemplate(absiTemplate).subscribe(data => {
        this.alert.success(data.name + ' was Created!');
        this.side.setStatus(data);
        this.version = data;
        this.side.version = data;

      },
        error => {
          console.log(error);
          this.alert.error(error.error);
        });
    }
  }

  duplicate() {
    let absiTemplate: AbsiTemplateResource = {
      name: 'ABSI - Copy',
      requesterId: this.getCurrentUser()?.id ?? -1,
      status: 0,
      type: this.type?.id ?? 0,
      wfTableID: this.version?.wfTableID
    };

    this._builder.CreateChecklistTemplate(absiTemplate).subscribe(data => {
      this.alert.success(this.version?.name + ' was Duplicated to ' + data.name + '!');
      this.version = data;
    });
  }

  delete() {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: "500px", data: {
        message: 'Are you sure you want to Delete this ABSI Template?',
        icon: "stop",
      },
    });
    confirm.afterClosed().subscribe(async (data) => {
      if (data) {
        if (this.version?.id) {
          const name = this.version.name;
          this._builder.DeleteVersion(this.version.id).subscribe(data => {
            this.alert.warning(name + ' was Deleted!');
            this.version = null;

          });
        }
      }
    });
  }

  /**
   * Approves the current hydrated template. Loading feedback is owned by the
   * shared loading service/interceptor, so this component only updates the model.
   */
  approve() {
    let version = this.version;
    if (version) {
      this._builder.ApproveVersion(version)
        .subscribe(data => {
          this.alert.success(version?.name + ' was Approved!!');
          this.version = data;
          // Approval changes the active workflow pointer on the template type.
          // Refresh it before another screen creates an ABSI from stale store data.
          this.store.dispatch(new AbsiTemplateTypeRefresh());
        }, error => {
          console.error(error);
          this.alert.defaultError();
        });
    }
  }


  @HostListener("window:resize")
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.innerWidth = window.innerWidth;
      if (this.innerWidth < 768) {
        this.isExpanded = false;
      }
    }, 200);
  }

  @HostListener("window:scroll")
  isHeaderOnTop() {
    let elemRec;
    if (this.headerTop?.nativeElement !== undefined) {
      elemRec = this.headerTop.nativeElement.getBoundingClientRect();
      const docViewTop = window.screenTop;
      const elemTop = elemRec.top;
      if (elemTop <= docViewTop) {
        this.headerOnTop = true;
      } else {
        this.headerOnTop = false;
      }
    } else {
      this.headerOnTop = false;
    }
  }

  //////
  changedFilter() { }
  clearFilter() { }
  selectedFilter() { }
  createDisabled() { }
  editDisabled() { }
  action() { }

  /**
   * Aggregates validation errors from every rendered section so the approve
   * action can be disabled and the template-level error list stays current.
   */
  checkErrors() {

    this.errorMessages = [];
    this.main?.sectionComponents.forEach((s) => {
      s.signatures?.forEach(t => {
        if (t.invalid) {
          const errorMessage =
            t.errors +
            " in Section: " +
            s.section.number +
            " " +
            s.section.name +
            ", Task: " +
            t.name;
          this.errorMessages?.push({ sectionId: s.section.id, stepId: t.sectionNumber ?? 0, taskId: t.id, errors: [errorMessage] });
        }
      });
    });

    this.hasErrors = this.errorMessages.length > 0;
    return this.hasErrors;
  }

  applyFilter(e: any) {
    if (!e)
      this.valueFilter = '';
    this.filter = e;
  }

  buttonsVisible: boolean | null = false;


}

